import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { getAis } from '../services/api';
import config from '../config';

interface Collection {
  _id: string;
  version: string;
  instruction?: string;
  chatbotTitle?: string;
  assistantName?: string;
  color?: string;
  debugOutline?: boolean;
}

interface AI {
  id: string;
  title: string;
  domain: string;
  description: string;
  collections: Collection[];
}

const TestWidget: React.FC = () => {
  const { aiId: paramAiId } = useParams<{ aiId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ais, setAis] = useState<AI[]>([]);
  const [selectedAi, setSelectedAi] = useState<AI | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const aiId = paramAiId || searchParams.get('aiId') || '';
  const collectionId = searchParams.get('collectionId') || '';

  const fetchAis = async () => {
    try {
      setLoading(true);
      const response = await getAis();
      setAis(response.data);
    } catch (err) {
      setError('Failed to load AIs');
      console.error('Error loading AIs:', err);
    } finally {
      setLoading(false);
    }
  };

  const initializeSelection = useCallback(() => {
    // Find the AI
    let targetAi = ais.find(ai => ai.id === aiId);
    if (!targetAi && ais.length > 0) {
      targetAi = ais[0]; // Default to first AI
    }

    if (targetAi) {
      setSelectedAi(targetAi);

      // Find the collection
      let targetCollection = targetAi.collections.find(col => col._id === collectionId);
      if (!targetCollection && targetAi.collections.length > 0) {
        targetCollection = targetAi.collections[0]; // Default to first collection
      }

      if (targetCollection) {
        setSelectedCollection(targetCollection);
      }
    }
  }, [ais, aiId, collectionId]);

  const loadWidget = useCallback(() => {
    if (!selectedAi || !selectedCollection) return;

    setConversationId(null); // Reset conversation ID

    // Clear any existing widget
    const existingWidget = document.getElementById('coachable-ai-widget-container');
    if (existingWidget) {
      existingWidget.remove();
    }

    // Remove existing script
    const existingScript = document.querySelector('script[data-coachable-widget]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create widget container with proper data attributes
    const widgetContainer = document.createElement('div');
    widgetContainer.id = 'coachable-ai-widget-container';
    widgetContainer.style.position = 'fixed';
    widgetContainer.style.bottom = '20px';
    widgetContainer.style.right = '20px';
    widgetContainer.style.zIndex = '1000';

    // Add the required data attributes that the widget script looks for
    widgetContainer.setAttribute('data-ai-id', selectedAi.id);
    widgetContainer.setAttribute('data-collection-id', selectedCollection._id);

    document.body.appendChild(widgetContainer);

    // Load the widget script
    const script = document.createElement('script');
    script.src = `${config.apiBaseUrl}/widget/widget.js?aiId=${selectedAi.id}&collectionId=${selectedCollection._id}&t=${Date.now()}`;
    script.setAttribute('data-coachable-widget', 'true');
    // Also add data attributes to the script tag
    script.setAttribute('data-ai-id', selectedAi.id);
    script.setAttribute('data-collection-id', selectedCollection._id);

    script.onload = () => {
      console.log('✅ Widget script loaded successfully');
      setWidgetLoaded(true);

      // Set up message listener to capture conversation ID
      const messageHandler = (event: MessageEvent) => {
        if (event.data && event.data.type === 'COACHABLE_CONVERSATION_ID') {
          console.log('🆔 Received conversation ID from widget:', event.data.conversationId);
          setConversationId(event.data.conversationId);
        }
      };

      window.addEventListener('message', messageHandler);

      // Clean up listener when component unmounts
      return () => {
        window.removeEventListener('message', messageHandler);
      };
    };
    script.onerror = () => {
      console.error('❌ Failed to load widget script');
      setError('Failed to load widget script');
    };

    document.head.appendChild(script);
  }, [selectedAi, selectedCollection]);

  useEffect(() => {
    fetchAis();
  }, []);

  useEffect(() => {
    if (ais.length > 0) {
      initializeSelection();
    }
  }, [ais, aiId, collectionId, initializeSelection]);

  useEffect(() => {
    if (selectedAi && selectedCollection) {
      loadWidget();
    }
  }, [selectedAi, selectedCollection, loadWidget]);

  const handleAiChange = (event: SelectChangeEvent) => {
    const newAiId = event.target.value;
    const newAi = ais.find(ai => ai.id === newAiId);

    if (newAi) {
      setSelectedAi(newAi);
      setSelectedCollection(newAi.collections[0] || null);
      setSearchParams({ aiId: newAiId, collectionId: newAi.collections[0]?._id || '' });
    }
  };

  const handleCollectionChange = (event: SelectChangeEvent) => {
    const newCollectionId = event.target.value;
    const newCollection = selectedAi?.collections.find(col => col._id === newCollectionId);

    if (newCollection) {
      setSelectedCollection(newCollection);
      setSearchParams({ aiId: selectedAi!.id, collectionId: newCollectionId });
    }
  };

  const refreshWidget = () => {
    setWidgetLoaded(false);
    setConversationId(null);
    loadWidget();
  };

  const openReviewPage = () => {
    if (selectedAi && selectedCollection) {
      const reviewUrl = `/review/${selectedAi.id}/versions/${selectedCollection._id}`;
      window.open(reviewUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth="1200px" mx="auto">
      <Typography variant="h4" gutterBottom>
        Test Widget - Manual Mode Testing
      </Typography>

      <Typography variant="body1" color="textSecondary" gutterBottom>
        This page allows you to test the chat widget and manual mode functionality in a controlled environment.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Widget Configuration
        </Typography>

        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select AI</InputLabel>
            <Select
              value={selectedAi?.id || ''}
              label="Select AI"
              onChange={handleAiChange}
            >
              {ais.map((ai) => (
                <MenuItem key={ai.id} value={ai.id}>
                  {ai.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Version</InputLabel>
            <Select
              value={selectedCollection?._id || ''}
              label="Select Version"
              onChange={handleCollectionChange}
              disabled={!selectedAi}
            >
              {selectedAi?.collections.map((collection) => (
                <MenuItem key={collection._id} value={collection._id}>
                  {collection.version}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={refreshWidget}>
            Refresh Widget
          </Button>

          <Button
            variant="contained"
            onClick={openReviewPage}
            disabled={!selectedAi || !selectedCollection}
          >
            Open Review Page
          </Button>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip
            label={`AI: ${selectedAi?.title || 'None'}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Version: ${selectedCollection?.version || 'None'}`}
            color="secondary"
            variant="outlined"
          />
          <Chip
            label={widgetLoaded ? 'Widget Loaded' : 'Widget Loading'}
            color={widgetLoaded ? 'success' : 'warning'}
            variant="outlined"
          />
          {conversationId && (
            <Chip
              label={`Conversation: ${conversationId.substring(0, 8)}...`}
              color="info"
              variant="outlined"
              title={`Full ID: ${conversationId}`}
            />
          )}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
            />
          }
          label="Debug Mode (Show detailed info)"
        />
      </Paper>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Testing Instructions
            </Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li><strong>Start a conversation:</strong> Click the chat widget (bottom-right) and send a message</li>
                <li><strong>Open Review page:</strong> Click "Open Review Page" button above</li>
                <li><strong>Enable manual mode:</strong> In the review page, toggle manual mode ON</li>
                <li><strong>Test manual mode:</strong> Send another message in the widget - it should show "waiting for trainer"</li>
                <li><strong>Send manual response:</strong> Use the review page to send a manual response</li>
                <li><strong>Verify real-time:</strong> The manual response should appear in the widget immediately</li>
              </ol>
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Expected Behavior
            </Typography>
            <Typography variant="body2" component="div">
              <ul>
                <li><strong>Normal mode:</strong> Messages get AI responses</li>
                <li><strong>Manual mode ON:</strong> Messages show "waiting for trainer" response</li>
                <li><strong>Manual response:</strong> Trainer messages appear in widget real-time</li>
                <li><strong>Manual mode OFF:</strong> Back to AI responses</li>
                <li><strong>Socket.io:</strong> Real-time communication should work bidirectionally</li>
              </ul>
            </Typography>
          </CardContent>
        </Card>

        {debugMode && (
          <Card sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Debug Information
              </Typography>
              <Typography variant="body2" component="pre" sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto'
              }}>
{`Current Configuration:
- AI ID: ${selectedAi?.id || 'Not selected'}
- Collection ID: ${selectedCollection?._id || 'Not selected'}
- Conversation ID: ${conversationId || 'Not available yet - send a message first'}
- Widget URL: ${config.apiBaseUrl}/widget/widget.js?aiId=${selectedAi?.id}&collectionId=${selectedCollection?._id}
- Widget Loaded: ${widgetLoaded}
- Backend: ${config.apiBaseUrl}
- Frontend: http://localhost:3000

Manual Mode Flow:
1. Widget sends message → /user/chat endpoint
2. Backend checks conversation.manual_mode in Supabase
3. If manual mode: returns "waiting for trainer" + emits to Socket.io room
4. Trainer sends response → /manual/conversations/:id/response endpoint
5. Backend emits manual response to Socket.io room
6. Widget receives real-time update

Socket.io Rooms:
- Room name format: conversation_{supabase_conversation_id}
- Trainer joins: conversation_117f7696-06ff-4c2c-838a-8bbd994098a3
- Widget joins: conversation_117f7696-06ff-4c2c-838a-8bbd994098a3`}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> The chat widget will appear in the bottom-right corner of this page.
          Make sure to test the full manual mode flow as described above. Check browser console for detailed logs.
        </Typography>
      </Alert>
    </Box>
  );
};

export default TestWidget;